import type { Task } from '../../../services/projectManagement/tasksService';
import type { TaskDependency } from '../../../services/projectManagement/dependenciesService';

type DayIndex = number;

export type PmDependencyConstraintType = 'finish_to_start' | 'none';

export interface PmScheduleViolation {
  from_task_id: string;
  to_task_id: string;
  type: PmDependencyConstraintType;
  message: string;
}

export interface PmScheduleAnalysis {
  criticalTaskIds: string[];
  violationTaskIds: string[];
  violationEdges: Array<{ from_task_id: string; to_task_id: string }>;
  violations: PmScheduleViolation[];
  slackByTaskId: Record<string, number>;
}

export type PmTaskDateUpdate = { start_date: string; due_date: string };

export type PmResizeEdge = 'start' | 'end';

function parseDateOrNull(value?: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateToIsoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(date: Date, deltaDays: number): Date {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d;
}

function diffDaysCeil(a: Date, b: Date): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.ceil((b.getTime() - a.getTime()) / msPerDay);
}

function getTaskDurationDays(task: Task): number {
  const estimatedHours = Number(task.estimated_time || 0);
  if (Number.isFinite(estimatedHours) && estimatedHours > 0) {
    return Math.max(1, Math.ceil(estimatedHours / 8));
  }
  return 1;
}

function getTaskPlannedDates(task: Task): { start: Date | null; end: Date | null } {
  const start = parseDateOrNull(task.start_date);
  const due = parseDateOrNull(task.due_date);

  if (start && due) {
    return start <= due ? { start, end: due } : { start: due, end: start };
  }

  const dur = getTaskDurationDays(task);
  if (start && !due) {
    return { start, end: addDays(start, dur) };
  }

  if (!start && due) {
    return { start: addDays(due, -dur), end: due };
  }

  return { start: null, end: null };
}

function dependencyToConstraintType(dep: TaskDependency): PmDependencyConstraintType {
  if (dep.dependency_type === 'relates-to') return 'none';
  return 'finish_to_start';
}

function buildGraph(taskIds: string[], deps: TaskDependency[]) {
  const nodes = new Set(taskIds);
  const incomingCount: Record<string, number> = {};
  const outgoing: Record<string, string[]> = {};
  const incoming: Record<string, string[]> = {};

  taskIds.forEach(id => {
    incomingCount[id] = 0;
    outgoing[id] = [];
    incoming[id] = [];
  });

  deps.forEach(dep => {
    if (!nodes.has(dep.depends_on_task_id) || !nodes.has(dep.task_id)) return;
    if (dependencyToConstraintType(dep) === 'none') return;

    const from = dep.depends_on_task_id;
    const to = dep.task_id;

    outgoing[from].push(to);
    incoming[to].push(from);
    incomingCount[to] += 1;
  });

  return { incomingCount, outgoing, incoming };
}

function topoSort(taskIds: string[], deps: TaskDependency[]): { order: string[]; hasCycle: boolean } {
  const { incomingCount, outgoing } = buildGraph(taskIds, deps);
  const queue: string[] = [];
  taskIds.forEach(id => {
    if ((incomingCount[id] || 0) === 0) queue.push(id);
  });

  const order: string[] = [];
  while (queue.length > 0) {
    const node = queue.shift()!;
    order.push(node);

    (outgoing[node] || []).forEach(next => {
      incomingCount[next] -= 1;
      if (incomingCount[next] === 0) queue.push(next);
    });
  }

  return { order, hasCycle: order.length !== taskIds.length };
}

export function analyzePmSchedule(tasks: Task[], deps: TaskDependency[]): PmScheduleAnalysis {
  const taskIds = tasks.map(t => t.id);
  const taskById: Record<string, Task> = {};
  tasks.forEach(t => {
    taskById[t.id] = t;
  });

  const { order, hasCycle } = topoSort(taskIds, deps);

  const durations: Record<string, number> = {};
  taskIds.forEach(id => {
    const t = taskById[id];
    durations[id] = t ? getTaskDurationDays(t) : 1;
  });

  const { incoming, outgoing } = buildGraph(taskIds, deps);

  const earliestStart: Record<string, DayIndex> = {};
  const earliestFinish: Record<string, DayIndex> = {};
  taskIds.forEach(id => {
    earliestStart[id] = 0;
    earliestFinish[id] = durations[id];
  });

  if (!hasCycle) {
    order.forEach(id => {
      const preds = incoming[id] || [];
      const es = preds.reduce((max, p) => Math.max(max, earliestFinish[p] || 0), 0);
      earliestStart[id] = es;
      earliestFinish[id] = es + durations[id];
    });
  }

  const projectFinish = hasCycle ? 0 : taskIds.reduce((max, id) => Math.max(max, earliestFinish[id] || 0), 0);

  const latestFinish: Record<string, DayIndex> = {};
  const latestStart: Record<string, DayIndex> = {};
  taskIds.forEach(id => {
    latestFinish[id] = projectFinish;
    latestStart[id] = Math.max(0, projectFinish - durations[id]);
  });

  if (!hasCycle) {
    const rev = [...order].reverse();
    rev.forEach(id => {
      const succ = outgoing[id] || [];
      const lf = succ.length === 0
        ? projectFinish
        : succ.reduce((min, s) => Math.min(min, latestStart[s]), projectFinish);
      latestFinish[id] = lf;
      latestStart[id] = lf - durations[id];
    });
  }

  const slackByTaskId: Record<string, number> = {};
  taskIds.forEach(id => {
    slackByTaskId[id] = hasCycle ? 0 : (latestStart[id] - earliestStart[id]);
  });

  const criticalTaskIds = hasCycle ? [] : taskIds.filter(id => slackByTaskId[id] === 0);

  const violations: PmScheduleViolation[] = [];
  const violationEdges: Array<{ from_task_id: string; to_task_id: string }> = [];
  const violationTaskIdsSet = new Set<string>();

  if (hasCycle) {
    violations.push({
      from_task_id: '',
      to_task_id: '',
      type: 'none',
      message: 'Circular dependency detected. Critical path and constraint checks are disabled until the cycle is removed.',
    });
  } else {
    deps.forEach(dep => {
      if (dependencyToConstraintType(dep) === 'none') return;

      const fromTask = taskById[dep.depends_on_task_id];
      const toTask = taskById[dep.task_id];
      if (!fromTask || !toTask) return;

      const fromDates = getTaskPlannedDates(fromTask);
      const toDates = getTaskPlannedDates(toTask);
      if (!fromDates.end || !toDates.start) return;

      if (toDates.start.getTime() < fromDates.end.getTime()) {
        violations.push({
          from_task_id: dep.depends_on_task_id,
          to_task_id: dep.task_id,
          type: 'finish_to_start',
          message: `"${toTask.title}" starts (${dateToIsoDay(toDates.start)}) before "${fromTask.title}" finishes (${dateToIsoDay(fromDates.end)}).`,
        });
        violationEdges.push({ from_task_id: dep.depends_on_task_id, to_task_id: dep.task_id });
        violationTaskIdsSet.add(dep.depends_on_task_id);
        violationTaskIdsSet.add(dep.task_id);
      }
    });
  }

  return {
    criticalTaskIds,
    violations,
    violationEdges,
    violationTaskIds: Array.from(violationTaskIdsSet),
    slackByTaskId,
  };
}

export function computeShiftedTaskDates(task: Task, deltaDays: number): { start_date?: string; due_date?: string } {
  const { start, end } = getTaskPlannedDates(task);
  if (!start || !end) return {};

  const nextStart = addDays(start, deltaDays);
  const nextEnd = addDays(end, deltaDays);

  return {
    start_date: dateToIsoDay(nextStart),
    due_date: dateToIsoDay(nextEnd),
  };
}

export function computeResizedTaskDates(task: Task, edge: PmResizeEdge, deltaDays: number): { start_date?: string; due_date?: string } {
  const { start, end } = getTaskPlannedDates(task);
  if (!start || !end) return {};

  if (edge === 'start') {
    const nextStart = addDays(start, deltaDays);
    const boundedStart = nextStart.getTime() > end.getTime() ? end : nextStart;
    return { start_date: dateToIsoDay(boundedStart), due_date: dateToIsoDay(end) };
  }

  const nextEnd = addDays(end, deltaDays);
  const boundedEnd = nextEnd.getTime() < start.getTime() ? start : nextEnd;
  return { start_date: dateToIsoDay(start), due_date: dateToIsoDay(boundedEnd) };
}

export function planAutoPushSuccessors(
  tasks: Task[],
  deps: TaskDependency[],
  changedTaskId: string
): Array<{ taskId: string; update: PmTaskDateUpdate }> {
  const taskById: Record<string, Task> = {};
  tasks.forEach(t => {
    taskById[t.id] = t;
  });

  const taskIds = tasks.map(t => t.id);

  const preds: Record<string, string[]> = {};
  const succs: Record<string, string[]> = {};
  taskIds.forEach(id => {
    preds[id] = [];
    succs[id] = [];
  });

  deps.forEach(dep => {
    if (dependencyToConstraintType(dep) === 'none') return;
    if (!taskById[dep.depends_on_task_id] || !taskById[dep.task_id]) return;
    preds[dep.task_id].push(dep.depends_on_task_id);
    succs[dep.depends_on_task_id].push(dep.task_id);
  });

  const reachable = new Set<string>();
  const q: string[] = [changedTaskId];
  reachable.add(changedTaskId);
  while (q.length > 0) {
    const id = q.shift()!;
    (succs[id] || []).forEach(s => {
      if (reachable.has(s)) return;
      reachable.add(s);
      q.push(s);
    });
  }

  const { order, hasCycle } = topoSort(taskIds, deps);
  if (hasCycle) return [];

  const plannedStart: Record<string, Date | null> = {};
  const plannedEnd: Record<string, Date | null> = {};
  const durations: Record<string, number> = {};

  taskIds.forEach(id => {
    const t = taskById[id];
    durations[id] = t ? getTaskDurationDays(t) : 1;
    const dates = t ? getTaskPlannedDates(t) : { start: null, end: null };
    plannedStart[id] = dates.start;
    plannedEnd[id] = dates.end;
  });

  const updates: Array<{ taskId: string; update: PmTaskDateUpdate }> = [];

  order.forEach(id => {
    if (!reachable.has(id) || id === changedTaskId) return;
    const p = preds[id] || [];
    if (p.length === 0) return;

    const requiredStart = p.reduce<Date | null>((acc, pid) => {
      const pe = plannedEnd[pid];
      if (!pe) return acc;
      if (!acc) return pe;
      return pe.getTime() > acc.getTime() ? pe : acc;
    }, null);

    if (!requiredStart) return;

    let s = plannedStart[id];
    let e = plannedEnd[id];

    if (!s || !e) {
      const dur = durations[id] || 1;
      s = requiredStart;
      e = addDays(requiredStart, dur);
    } else if (s.getTime() < requiredStart.getTime()) {
      const shiftDays = diffDaysCeil(s, requiredStart);
      s = addDays(s, shiftDays);
      e = addDays(e, shiftDays);
    } else {
      return;
    }

    plannedStart[id] = s;
    plannedEnd[id] = e;

    updates.push({
      taskId: id,
      update: { start_date: dateToIsoDay(s), due_date: dateToIsoDay(e) },
    });
  });

  return updates;
}
